/**
 * Phase 10 R2 — useComputeJobLabor
 *
 * Two modes:
 *   1. Per-row: compute labor for a single uncomputed ticket_jobs row.
 *   2. Bulk:    compute labor for ALL uncomputed rows on a ticket.
 *
 * Strict null-only fill (matches R2 safety contract):
 *   • Skips rows where labor_sell_amount IS NOT NULL (already computed)
 *   • Skips rows where labor_rate_overridden = true (regardless of state)
 *   • NEVER overwrites, recomputes, or refreshes existing values
 *
 * AUTHORITY (FF-A):
 *   The `labor_engine_enabled` flag + `ui_ticket_financials` exposure
 *   are UI gates only. Server authority is enforced by the
 *   `trg_enforce_labor_entitlement` trigger on `ticket_jobs` (migration
 *   `20260501120000_ff_a_labor_entitlement_trigger.sql`), which raises
 *   `insufficient_privilege` if the parent shop is not CATALYST/ENTERPRISE
 *   tier and lacks the `labor_engine` addon. A sessionStorage flag
 *   override cannot bypass that trigger.
 *
 * Key shape: ticket_jobs has composite PK (ticket_id, job_id).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { useShopLaborRates } from '@/hooks/labor/useShopLaborRates';
import { computeJobLabor, laborColumnsForUpsert } from '@/lib/labor/computeJobLabor';

interface ComputeArgs {
  ticketId: string;
  /** When provided, only this single ticket_jobs row is touched. */
  jobId?: string;
}

export function useComputeJobLabor() {
  const qc = useQueryClient();
  const laborEnabled = useFeatureFlag('labor_engine_enabled');
  const financialsEnabled = useExposureFlag('ui_ticket_financials');
  const { data: shopRates } = useShopLaborRates();

  return useMutation({
    mutationFn: async (args: ComputeArgs) => {
      if (!laborEnabled || !financialsEnabled) {
        toast({
          title: 'Labor financials disabled',
          description: 'Labor financials are disabled for this shop.',
          variant: 'destructive',
        });
        return { skipped: true as const, written: 0 };
      }
      if (!shopRates) {
        toast({
          title: 'Shop labor rates not loaded',
          description: 'Try again in a moment.',
          variant: 'destructive',
        });
        return { skipped: true as const, written: 0 };
      }

      // Server-side guard: only NULL labor_sell_amount AND NOT overridden.
      let q = (supabase as any)
        .from('ticket_jobs')
        .select(
          `ticket_id, job_id, minutes_override, jobs:job_id(standard_minutes, category)`,
        )
        .eq('ticket_id', args.ticketId)
        .is('deleted_at', null)
        .is('labor_sell_amount', null)
        .eq('labor_rate_overridden', false);

      if (args.jobId) q = q.eq('job_id', args.jobId);

      const { data: rows, error } = await q;
      if (error) throw error;

      let written = 0;
      for (const row of rows ?? []) {
        const effective =
          row.minutes_override ?? (row.jobs as any)?.standard_minutes ?? 0;
        const computed = computeJobLabor({
          effectiveMinutes: effective,
          jobCategory: (row.jobs as any)?.category ?? null,
          shopRates,
          override: null,
        });
        // Re-assert the WHERE guards on the UPDATE to defeat any race.
        const { error: updErr, count } = await (supabase as any)
          .from('ticket_jobs')
          .update(laborColumnsForUpsert(computed), { count: 'exact' })
          .eq('ticket_id', row.ticket_id)
          .eq('job_id', row.job_id)
          .is('labor_sell_amount', null)
          .eq('labor_rate_overridden', false);
        if (updErr) throw updErr;
        if (count) written += count;
      }
      return { skipped: false as const, written };
    },
    onSuccess: (res, args) => {
      if (res?.skipped) return;
      qc.invalidateQueries({ queryKey: ['ticket-labor-lines', args.ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket-jobs-for-parts', args.ticketId] });
      if (res.written > 0) {
        toast({
          title: 'Labor computed',
          description: `${res.written} job${res.written === 1 ? '' : 's'} updated.`,
        });
      }
    },
    onError: (e: any) => {
      toast({
        title: 'Failed to compute labor',
        description: e?.message ?? 'Unknown error',
        variant: 'destructive',
      });
    },
  });
}
